import { useState, useCallback } from 'react';

const useRetryableApi = (maxRetries = 3, retryDelay = 1000) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const executeWithRetry = useCallback(async (apiCall, onSuccess, onError) => {
    let attempt = 0;
    
    const tryRequest = async () => {
      try {
        setRetryCount(attempt);
        if (attempt > 0) {
          setIsRetrying(true);
        }
        
        const result = await apiCall();
        
        // Reset retry state on success
        setIsRetrying(false);
        setRetryCount(0);
        
        if (onSuccess) {
          onSuccess(result);
        }
        
        return result;
      } catch (error) {
        attempt++;
        
        // Check if it's a network error worth retrying
        const isRetryableError = (
          !error.response || // Network error
          error.response.status >= 500 || // Server error
          error.response.status === 429 // Rate limit
        );
        
        if (attempt <= maxRetries && isRetryableError) {
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
          return tryRequest();
        } else {
          // Max retries reached or non-retryable error
          setIsRetrying(false);
          setRetryCount(0);
          
          if (onError) {
            onError(error, attempt);
          }
          
          throw error;
        }
      }
    };

    return tryRequest();
  }, [maxRetries, retryDelay]);

  const getErrorMessage = useCallback((error, attemptCount) => {
    if (!error.response) {
      return `Erreur de connexion${attemptCount > 1 ? ` (${attemptCount} tentatives)` : ''}. Vérifiez votre connexion internet.`;
    }
    
    switch (error.response.status) {
      case 429:
        return `Trop de requêtes${attemptCount > 1 ? ` (${attemptCount} tentatives)` : ''}. Veuillez patienter.`;
      case 500:
      case 502:
      case 503:
      case 504:
        return `Erreur serveur${attemptCount > 1 ? ` (${attemptCount} tentatives)` : ''}. Veuillez réessayer.`;
      case 401:
        return 'Session expirée. Veuillez vous reconnecter.';
      case 403:
        return 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
      case 404:
        return 'Ressource introuvable.';
      default:
        return error.response.data?.message || `Erreur inconnue${attemptCount > 1 ? ` (${attemptCount} tentatives)` : ''}.`;
    }
  }, []);

  return {
    executeWithRetry,
    getErrorMessage,
    isRetrying,
    retryCount
  };
};

export default useRetryableApi;